<ng-container *ngIf="hasData; else emptyState">
	<section class="section-header">
		<div class="header-content">
			<div class="header-left">
				<i class="fa-solid fa-gauge-high"></i>
				<div class="header-text">
					<h1>Calculadora de Scoring de Riesgo</h1>
					<p class="header-subtitle">{{ pillars.length }} pilares ponderados + scores externos (Buró, Ekatena)</p>
				</div>
			</div>
			<div class="header-right">
				<p-tag [value]="decisionLabel" [severity]="decisionSeverity"
					[icon]="isApproved ? 'pi pi-check-circle' : (isRejected ? 'pi pi-times-circle' : 'pi pi-clock')">
				</p-tag>
			</div>
		</div>
	</section>

	<div class="scoring-summary">
		<!-- ── Card 1: Puntaje ── -->
		<div class="stat-card stat-score" [class.approved]="isApproved" [class.rejected]="isRejected">
			<div class="stat-head">
				<i class="fa-solid fa-chart-line"></i>
				<span class="stat-label">Puntaje obtenido</span>
			</div>
			<div class="stat-row stat-row-inline">
				<div class="stat-value">
					{{ calculator?.totalScore | number:'1.2-2' }}
					<span class="stat-unit">/ 100</span>
				</div>
				<div class="stat-progress" title="Posición del puntaje respecto al umbral">
					<div class="progress-track">
						<div class="progress-fill" [style.width.%]="scoreProgress"></div>
						<div class="progress-marker" [style.left.%]="thresholdProgress"></div>
					</div>
				</div>
				<div class="stat-side-badge" *ngIf="calculator?.acceptanceThreshold !== null && calculator?.acceptanceThreshold !== undefined">
					<i class="fa-solid fa-bullseye"></i>
					<strong>{{ calculator?.acceptanceThreshold | number:'1.0-0' }}</strong>
				</div>
			</div>
		</div>

		<!-- ── Card 2: Umbral ── -->
		<div class="stat-card stat-threshold">
			<div class="stat-head">
				<i class="fa-solid fa-bullseye"></i>
				<span class="stat-label">Umbral de aceptación</span>
			</div>
			<div class="stat-row">
				<div class="stat-value">
					{{ calculator?.acceptanceThreshold | number:'1.0-0' }}
					<span class="stat-unit">pts mín.</span>
				</div>
				<div class="stat-side-meta" *ngIf="pointsDifference !== null">
					<ng-container *ngIf="pointsDifference >= 0">
						<i class="fa-solid fa-arrow-up text-success"></i>
						<span class="text-success"><strong>+{{ pointsDifference | number:'1.2-2' }}</strong> pts sobre mín.</span>
					</ng-container>
					<ng-container *ngIf="pointsDifference < 0">
						<i class="fa-solid fa-arrow-down text-danger"></i>
						<span class="text-danger">Faltaron <strong>{{ -pointsDifference | number:'1.2-2' }}</strong> pts</span>
					</ng-container>
				</div>
			</div>
		</div>

		<!-- ── Card 3: Decisión ── -->
		<div class="stat-card stat-decision" [class.approved]="isApproved" [class.rejected]="isRejected">
			<div class="stat-head">
				<i class="fa-solid fa-scale-balanced"></i>
				<span class="stat-label">Decisión final</span>
			</div>
			<div class="stat-decision-body">
				<i class="stat-decision-icon fa-solid"
					[class.fa-circle-check]="isApproved"
					[class.fa-circle-xmark]="isRejected"
					[class.fa-circle-question]="!isApproved && !isRejected"></i>
				<div class="stat-decision-text">
					{{ decisionLabel }}
				</div>
			</div>
		</div>
	</div>

	<section class="info-card" *ngFor="let pillar of pillars; trackBy: trackByPillarKey">
		<div class="card-header">
			<i [class]="iconForPillar(pillar.key)"></i>
			<h3>{{ pillar.label }}</h3>
			<div class="header-tags">
				<p-tag [value]="(pillar.weight | percent:'1.0-0') + ''" severity="info"></p-tag>
				<p-tag [value]="'Subtotal: ' + (pillar.subtotal | number:'1.2-2')" severity="secondary"></p-tag>
			</div>
		</div>
		<div class="card-content" *ngIf="pillar.rows?.length">
			<table class="data-table">
				<thead>
					<tr>
						<th>Métrica</th>
						<th class="num">Valor</th>
						<th class="num">Proporción</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let row of pillar.rows; trackBy: trackByRowIndex"
						[class.subcategory]="row.isSubcategory">
						<td>{{ row.label }}</td>
						<td class="num">{{ row.value | number:'1.2-2' }}</td>
						<td class="num pct">{{ row.proportion | percent:'1.1-2' }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</section>

	<section class="info-card" *ngIf="calculator?.creditBureauScore || calculator?.creditBureauObligorScore || calculator?.ekatenaExternalScore">
		<div class="card-header">
			<i class="fa-solid fa-globe"></i>
			<h3>Scores Externos</h3>
		</div>
		<div class="card-content">
			<div class="compact-grid">
				<div class="external-block" *ngIf="calculator?.creditBureauScore">
					<div class="external-title">
						<i class="fa-solid fa-id-card"></i>
						<span>{{ calculator.creditBureauScore.label || 'Buró de Crédito' }}</span>
					</div>
					<div class="external-row">
						<span class="label">Score crudo</span>
						<span class="value">
							{{ calculator.creditBureauScore.raw | number:'1.0-0' }}
							<small>/ {{ calculator.creditBureauScore.scaleMax | number:'1.0-0' }}</small>
						</span>
					</div>
					<div class="external-row">
						<span class="label">Normalizado</span>
						<span class="value success">{{ calculator.creditBureauScore.normalized | number:'1.2-2' }}</span>
					</div>
				</div>
				<!-- Buró del Obligado Solidario — solo en análisis de Persona Física -->
				<div class="external-block" *ngIf="calculator?.creditBureauObligorScore">
					<div class="external-title">
						<i class="fa-solid fa-id-card-clip"></i>
						<span>{{ calculator.creditBureauObligorScore.label || 'Buró de Crédito Obligado' }}</span>
					</div>
					<div class="external-row">
						<span class="label">Score crudo</span>
						<span class="value">
							{{ calculator.creditBureauObligorScore.raw | number:'1.0-0' }}
							<small>/ {{ calculator.creditBureauObligorScore.scaleMax | number:'1.0-0' }}</small>
						</span>
					</div>
					<div class="external-row">
						<span class="label">Normalizado</span>
						<span class="value success">{{ calculator.creditBureauObligorScore.normalized | number:'1.2-2' }}</span>
					</div>
				</div>
				<div class="external-block" *ngIf="calculator?.ekatenaExternalScore">
					<div class="external-title">
						<i class="fa-solid fa-magnifying-glass-chart"></i>
						<span>{{ calculator.ekatenaExternalScore.label || 'Ekatena' }}</span>
					</div>
					<div class="external-row">
						<span class="label">Score crudo</span>
						<span class="value">
							{{ calculator.ekatenaExternalScore.raw | number:'1.0-0' }}
							<small>/ {{ calculator.ekatenaExternalScore.scaleMax | number:'1.0-0' }}</small>
						</span>
					</div>
					<div class="external-row">
						<span class="label">Normalizado</span>
						<span class="value success">{{ calculator.ekatenaExternalScore.normalized | number:'1.2-2' }}</span>
					</div>
				</div>
			</div>
		</div>
	</section>
</ng-container>

<ng-template #emptyState>
	<div class="empty-state">
		<i class="fa-solid fa-file-arrow-up"></i>
		<h3>Sin datos de la Calculadora</h3>
		<p>Sube el Excel de Análisis de Riesgo arriba para ver el scoring (Carácter / Capacidad / Capital / Colateral / adicional) y la decisión final.</p>
	</div>
</ng-template>
